import { ProjectLayout } from '@/components/projects'
import { Sidebar } from '@/components/Sidebar'
import { TenantSwitcher } from '@/components/TenantSwitcher'
import { Toaster } from '@/components/ui'
import { UserProfileMenu } from '@/components/UserProfileMenu'
import { SidebarLogo } from '@/components/BrandLogo'
import {
  BacklogPage, ChatPage, ConnectorsPage, ContextPage,
  IntentsPage, ProjectOverviewPage, ProjectSettingsPage, ProjectsPage,
  PromptsPage, PromptDetailPage, PromptConfigPage, SkillsPage, SkillDetailPage, SkillConfigPage, SkillAttachmentsPage, SkillVersionHistoryPage,
  AgentsPage, AgentDetailPage, AgentConfigPage, AgentFilesPage,
  AgentMemoryPage, AgentVersionHistoryPage, SettingsPage, LoginPage,
  WorkflowDefinitionsPage, WorkflowDefinitionEditorPage,
  ChainsPage, ChainEditorPage,
  WorkflowRunsListPage, WorkflowRunDetailPage, ChainRunDetailPage,
  WorkflowSchedulesPage, WorkflowScheduleDetailPage,
  TestPlansPage, TenantMembersPage, TenantSettingsPage, TestCasesOverviewPage,
  SandboxImagesAdminPage, AnalyticsPage, AutomationPage, EnvironmentVariablesPage,
} from '@/pages'
import { TenantSelectPage } from '@/pages/TenantSelectPage'
import { ChevronsLeft, ChevronsRight } from 'lucide-react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { ProtectedRoute } from '@/auth'
import { useTenant } from '@/contexts/TenantContext'
import { useSidebarCollapse } from '@/hooks'

function ProjectIndexRedirect() {
  return <Navigate to="chat" replace />
}

function LegacyDraftsRedirect() {
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  const legacyIntentId = params.get('draftId')
  if (legacyIntentId !== null) {
    params.delete('draftId')
    params.set('intentId', legacyIntentId)
  }
  const query = params.toString()
  return <Navigate to={`../intents${query ? `?${query}` : ''}${hash}`} replace />
}

function TenantGuard({ children }: { children: React.ReactNode }) {
  const { isResolved, isLoading } = useTenant()
  const location = useLocation()
  if (isLoading) return null
  if (!isResolved) {
    const from = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to="/select-tenant" state={{ from }} replace />
  }
  return <>{children}</>
}

function TenantSelectLayout() {
  const { collapsed, toggle } = useSidebarCollapse('sidebar-collapsed')
  return (
    <div className="flex min-h-screen app-min-width page-canvas">
      <aside className={`sticky top-0 z-20 h-screen shrink-0 ${collapsed ? 'w-16' : 'w-64'} border-r bg-card flex flex-col transition-all duration-200`}>
        {collapsed && (
          <button
            onClick={toggle}
            className="absolute top-[18px] left-full z-30 ml-1 flex h-7 w-7 items-center justify-center rounded-md border bg-card shadow-sm text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Expand sidebar"
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </button>
        )}
        <div className="flex h-16 items-center border-b px-4">
          <SidebarLogo collapsed={collapsed} />
          {!collapsed && (
            <button
              onClick={toggle}
              className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              aria-label="Collapse sidebar"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          )}
        </div>
        <TenantSwitcher collapsed={collapsed} />
        <div className="flex-1" />
        <UserProfileMenu showSettings={false} collapsed={collapsed} />
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      <Toaster />
    </div>
  )
}

function RootLayout() {
  const { collapsed, toggle } = useSidebarCollapse('sidebar-collapsed')
  return (
    <TenantGuard>
      <div className="flex min-h-screen app-min-width page-canvas">
        <Sidebar collapsed={collapsed} onToggle={toggle} />
        <main className="flex-1 min-w-0">
          <div className="p-8 max-w-[1560px] mx-auto">
            <Outlet />
          </div>
        </main>
        <Toaster />
      </div>
    </TenantGuard>
  )
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute><TenantSelectLayout /></ProtectedRoute>}>
        <Route path="/select-tenant" element={<TenantSelectPage />} />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <RootLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/tenant-members" element={<TenantMembersPage />} />
        <Route path="/tenant/settings" element={<TenantSettingsPage />} />
        <Route path="/sandbox-images" element={<SandboxImagesAdminPage />} />
      </Route>
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <TenantGuard>
              <ProjectLayout />
            </TenantGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<ProjectIndexRedirect />} />
        <Route path="context" element={<ContextPage />} />
        <Route path="overview" element={<ProjectOverviewPage />} />
        <Route path="backlog" element={<BacklogPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="connectors" element={<ConnectorsPage />} />
        <Route path="settings" element={<ProjectSettingsPage />} />
        <Route path="workflows/prompts" element={<PromptsPage />} />
        <Route path="workflows/prompts/:promptId" element={<PromptDetailPage />}>
          <Route index element={<Navigate to="config" replace />} />
          <Route path="config" element={<PromptConfigPage />} />
        </Route>
        <Route path="workflows/skills" element={<SkillsPage />} />
        <Route path="workflows/skills/:skillId" element={<SkillDetailPage />}>
          <Route index element={<Navigate to="config" replace />} />
          <Route path="config" element={<SkillConfigPage />} />
          <Route path="attachments" element={<SkillAttachmentsPage />} />
          <Route path="version-history" element={<SkillVersionHistoryPage />} />
        </Route>
        <Route path="workflows/agents" element={<AgentsPage />} />
        <Route path="workflows/agents/:agentId" element={<AgentDetailPage />}>
          <Route index element={<Navigate to="config" replace />} />
          <Route path="config" element={<AgentConfigPage />} />
          <Route path="files" element={<AgentFilesPage />} />
          <Route path="memory" element={<AgentMemoryPage />} />
          <Route path="version-history" element={<AgentVersionHistoryPage />} />
        </Route>
        <Route path="intents" element={<IntentsPage />} />
        <Route path="drafts" element={<LegacyDraftsRedirect />} />
        <Route path="workflows/definitions" element={<WorkflowDefinitionsPage />} />
        <Route path="workflows/definitions/new" element={<WorkflowDefinitionEditorPage />} />
        <Route path="workflows/definitions/:definitionId" element={<WorkflowDefinitionEditorPage />} />
        <Route path="workflows/runs" element={<WorkflowRunsListPage />} />
        <Route path="workflows/runs/chains/:chainRunId" element={<ChainRunDetailPage />} />
        <Route path="workflows/runs/:runId" element={<WorkflowRunDetailPage />} />
        <Route path="workflows/schedules" element={<WorkflowSchedulesPage />} />
        <Route path="workflows/schedules/:scheduleId" element={<WorkflowScheduleDetailPage />} />
        <Route path="chains" element={<ChainsPage />} />
        <Route path="chains/new" element={<ChainEditorPage />} />
        <Route path="chains/:chainId" element={<ChainEditorPage />} />
        <Route path="testing-integration">
          <Route index element={<Navigate to="test-plans" replace />} />
          <Route path="test-plans" element={<TestPlansPage />} />
          <Route path="test-suites/:testSuiteId" element={<TestCasesOverviewPage />} />
          <Route path="automation" element={<AutomationPage />} />
          <Route path="environment-variables" element={<EnvironmentVariablesPage />} />
        </Route>
        <Route path="analytics" element={<AnalyticsPage />} />
      </Route>
    </Routes>
  )
}

export default App
